/** @format */

import { useTranslation } from "react-i18next";
import { PaperGroup } from "../hooks/usePaperQuestions";
import { getQuestionContentText, hasDisplayableContent } from "@question-bank/shared";
import { MathText } from "./MathText";
import DragIcon from "../assets/icons/DragIcon.svg?react";
import { PaperEntry, countEntryParts } from "../utils/paper";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { toLangCode } from "../utils/language";

interface StructureViewProps {
	groups: PaperGroup[];
	onRemove: (entry: PaperEntry) => void;
	onPreview: (group: PaperGroup) => void;
	onEdit: (group: PaperGroup) => void;
	onReorder: (from: number, to: number) => void;
}

const infoStyles =
	"font-roboto font-normal text-[10px] text-(--public-color-text-lighter) leading-[150%] flex flex-row gap-1 items-center";

export const StructureView = ({ groups, onRemove, onPreview, onEdit, onReorder }: StructureViewProps) => {
	const { t, i18n } = useTranslation();
	const activeLang = i18n.language?.toLowerCase().startsWith("cy") ? "cymraeg" : "english";

	const renderableGroups = groups
		.map((group, originalIndex) => ({ group, originalIndex }))
		.filter(({ group }) => group.questions.some(hasDisplayableContent));

	const handleDragEnd = (result: DropResult) => {
		if (!result.destination) {
			return;
		}
		const from = renderableGroups[result.source.index].originalIndex;
		const to = renderableGroups[result.destination.index].originalIndex;
		onReorder(from, to);
	};

	return (
		<div>
			<DragDropContext onDragEnd={handleDragEnd}>
				<Droppable droppableId="structure-view-groups">
					{(droppableProvided) => (
						<ul ref={droppableProvided.innerRef} {...droppableProvided.droppableProps} className="flex flex-col gap-5">
							{renderableGroups.map(({ group, originalIndex }, index) => {
								const firstQuestion = group.questions.find(hasDisplayableContent)!;
								const previewText = getQuestionContentText(firstQuestion, toLangCode(activeLang));
								const duration = group.entry.totalMarks;
								const parts = countEntryParts(group.entry);

								return (
									<Draggable
										key={group.entry.parentQuestionId}
										draggableId={String(group.entry.parentQuestionId)}
										index={index}
									>
										{(draggableProvided) => (
											<li
												data-testid="question-item"
												ref={draggableProvided.innerRef}
												{...draggableProvided.draggableProps}
												{...draggableProvided.dragHandleProps}
												className="flex flex-row gap-5 bg-white rounded-tl-[10px] rounded-bl-[10px] pr-4 h-14.75 items-center align-middle"
											>
												<span className="cursor-grab">
													<DragIcon />
												</span>

												<div
													key={firstQuestion.id}
													className="grid grid-cols-[auto_1fr_auto] gap-3 items-baseline text-[13px] text-(--public-color-text-default) font-lato align-middle flex-1 min-w-0"
												>
													<span className="font-lato font-black text-[16px] leading-[100%] truncate min-w-0">
														{originalIndex + 1}
													</span>
													{previewText && (
														<span className="font-roboto text-(--public-color-text-default) truncate">
															<MathText text={previewText} />
														</span>
													)}
													{parts.total > 0 && (
														<span className={`${infoStyles} whitespace-nowrap`}>
															({t("structureView.partsLabel", { count: parts.selected, total: parts.total })})
														</span>
													)}
												</div>
												<div className="flex flex-row gap-5 items-center justify-between ml-2 shrink-0 w-70">
													<button
														aria-label={t("structureView.previewQuestion")}
														onClick={() => onPreview(group)}
														className="cursor-pointer"
													>
														<svg
															xmlns="http://www.w3.org/2000/svg"
															viewBox="0 0 24 24"
															fill="currentColor"
															className="size-5"
														>
															<path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
															<path
																fillRule="evenodd"
																d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 0 1 0-1.113ZM17.25 12a5.25 5.25 0 1 1-10.5 0 5.25 5.25 0 0 1 10.5 0Z"
																clipRule="evenodd"
															/>
														</svg>
													</button>
													<button
														aria-label={t("structureView.editQuestion")}
														onClick={() => onEdit(group)}
														className="cursor-pointer"
													>
														<svg
															xmlns="http://www.w3.org/2000/svg"
															fill="none"
															viewBox="0 0 24 24"
															strokeWidth={1.5}
															stroke="#4B585D"
															className="size-5"
														>
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
															/>
														</svg>
													</button>

													<span className={infoStyles}>
														<svg
															xmlns="http://www.w3.org/2000/svg"
															fill="none"
															viewBox="0 0 24 24"
															strokeWidth={1.5}
															stroke="currentColor"
															className="size-3.5"
														>
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
															/>
														</svg>
														{duration} {t("structureView.minutes")}
													</span>
													<span className={infoStyles}>
														<svg
															xmlns="http://www.w3.org/2000/svg"
															width="11"
															height="12"
															viewBox="0 0 16 16"
															fill="none"
														>
															<g clipPath="url(#clip0_3156_5464)">
																<path
																	d="M14 1C14.2652 1 14.5196 1.10536 14.7071 1.29289C14.8946 1.48043 15 1.73478 15 2V14C15 14.2652 14.8946 14.5196 14.7071 14.7071C14.5196 14.8946 14.2652 15 14 15H2C1.73478 15 1.48043 14.8946 1.29289 14.7071C1.10536 14.5196 1 14.2652 1 14V2C1 1.73478 1.10536 1.48043 1.29289 1.29289C1.48043 1.10536 1.73478 1 2 1H14ZM2 0C1.46957 0 0.960859 0.210714 0.585786 0.585786C0.210714 0.960859 0 1.46957 0 2L0 14C0 14.5304 0.210714 15.0391 0.585786 15.4142C0.960859 15.7893 1.46957 16 2 16H14C14.5304 16 15.0391 15.7893 15.4142 15.4142C15.7893 15.0391 16 14.5304 16 14V2C16 1.46957 15.7893 0.960859 15.4142 0.585786C15.0391 0.210714 14.5304 0 14 0L2 0Z"
																	fill="#767676"
																/>
																<path
																	d="M10.9702 4.97028C11.1101 4.83155 11.299 4.75348 11.4961 4.75293C11.6931 4.75239 11.8825 4.82942 12.0232 4.96737C12.1639 5.10533 12.2447 5.29312 12.248 5.49015C12.2514 5.68718 12.1771 5.87761 12.0412 6.02028L8.04916 11.0103C7.98055 11.0842 7.89774 11.1435 7.80569 11.1846C7.71364 11.2258 7.61423 11.248 7.51341 11.2499C7.41259 11.2517 7.31243 11.2332 7.21891 11.1955C7.1254 11.1578 7.04046 11.1016 6.96916 11.0303L4.32416 8.38428C4.25047 8.31562 4.19137 8.23282 4.15038 8.14082C4.10938 8.04882 4.08734 7.94951 4.08557 7.8488C4.08379 7.7481 4.10231 7.64807 4.14004 7.55468C4.17776 7.46129 4.2339 7.37646 4.30512 7.30524C4.37634 7.23402 4.46117 7.17788 4.55456 7.14016C4.64795 7.10244 4.74798 7.08391 4.84868 7.08569C4.94938 7.08747 5.0487 7.10951 5.1407 7.1505C5.2327 7.19149 5.3155 7.25059 5.38416 7.32428L7.47816 9.41728L10.9512 4.99228L10.9702 4.97028Z"
																	fill="#767676"
																/>
															</g>
															<defs>
																<clipPath id="clip0_3156_5464">
																	<rect width="16" height="16" fill="white" />
																</clipPath>
															</defs>
														</svg>
														{group.entry.totalMarks} {t("structureView.marks")}
													</span>
													<button
														aria-label={t("structureView.removeQuestion")}
														onClick={() => onRemove(group.entry)}
														className="cursor-pointer"
													>
														<svg
															xmlns="http://www.w3.org/2000/svg"
															fill="none"
															viewBox="0 0 24 24"
															strokeWidth={1.5}
															stroke="#4B585D"
															className="size-5"
														>
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
															/>
														</svg>
													</button>
												</div>
											</li>
										)}
									</Draggable>
								);
							})}
							{droppableProvided.placeholder}
						</ul>
					)}
				</Droppable>
			</DragDropContext>
		</div>
	);
};
